import React, {useEffect, useRef, useState} from 'react';
import Translate, {t} from "../../../../../../../components/Translate";
import ColorIndicator from "../../../../../../../components-new/general/ColorIndicator";
import {
    formatSecondsToHHMMSS,
    globalExportToExcel,
    hasManagementPermission
} from "../../../../../../../utils/General";
import ExcelButton from "../../../../../../../components-new/buttons/ExcelButton";
import RatingStars from "../../../../../../../components-new/general/RatingStars";
import LevelBadge from "../../../../../../../components-new/general/LevelBadge";
import Tooltip from "../../../../../../../components-new/general/Tooltip";
import ResetButton from "../../../../../../../components-new/buttons/ResetButton";
import {getOperatorsTableData} from "../../../../../../../services/statistics/api";
import LoadingComponent from "../../../../../../../components-new/ui/LoadingComponent";
import TableHeaderSort from "../../../../../../../components-new/table/TableHeaderSort";
import Colors from "../../../../../../../utils/Colors";
import {handleSort} from "../../../../../../../utils/TableUtils";
import config from "../../../../../../../config";
import DatePicker from "../../../../../../../components-new/form/DatePicker";
import SearchInput from "../../../../../../../components-new/form/SearchInput";
import Tabs from "../../../../../../../components-new/ui/Tabs";
import TableScroll from "../../../../../../../components-new/table/TableScroll";
import ContactAvatar from "../../../../../../../components-new/general/ContactAvatar";
import OperatorProfileLink from "../../../../../../../components-new/general/OperatorProfileLink";
import OperatorProfileDrawer from "../../../../../../../components-new/general/OperatorProfileDrawer";

const tabOptions = {
    general: 'main',
    requests: 'requests',
    time: 'time',
};
const tableHeaders = {
    'main': [
        {text: 'operator', sortColumn: 'firstName,lastName', width: '220px'},
        {text: 'currentStatus', sortColumn: 'currentStatus', align: 'center', width: '280px'},
        {text: 'Rating', sortColumn: 'rating', align: 'center', width: '320px'},
        {text: 'currentChatLimit', sortColumn: 'currentChatLimit', align: 'center', width: '190px'},
        {text: 'chatLimit', sortColumn: 'chatLimit', align: 'center', width: '190px'},
    ],
    'requests': [
        {text: 'operator', sortColumn: 'firstName'},
        {text: 'allRequests', sortColumn: 'allChats', align: 'center'},
        {text: 'todaysRequests', sortColumn: 'todaysChats', align: 'center'},
        {text: 'answeredRequestsToOperator', sortColumn: 'answeredChats', align: 'center'},
        {text: 'unansweredRequestsToOperator', sortColumn: 'unansweredChats', align: 'center'},
        {text: 'activeRequests', sortColumn: 'activeChats', align: 'center'},
        {text: 'forwardedRequestsToOperator', sortColumn: 'forwardedChats', align: 'center'},
    ],
    'time': [
        {text: 'operator', sortColumn: 'firstName'},
        {text: 'avrgAnswerSpeed', sortColumn: 'avrgAnswerSpeed', align: 'center'},
        {text: 'avrgFirstAnswerSpeed', sortColumn: 'avrgFirstAnswerSpeed', align: 'center'},
        {text: 'avrgConvTime', sortColumn: 'avrgConvTime', align: 'center'},
    ]
};

const OperatorsTable = ({
                            openTrModal,
                            company,
                            tariff,
                            user
                        }) => {
    const [activeTab, setActiveTab] = useState(tabOptions.general);

    const [filters, setFilters] = useState({
        companyIds: company.subs ? company.subs.map(sub => sub.id) : [],
        start: null,
        end: null,
        operator: ''
    })

    const [pageable, setPageable] = useState({sort: ''});
    const [isFetching, setIsFetching] = useState(false);
    const [operators, setOperators] = useState([]);
    const [operatorSearchValue, setOperatorSearchValue] = useState('')

    /* whose profile is open, or null - the same drawer for all three tabs */
    const [operatorProfile, setOperatorProfile] = useState(null);
    const canViewOperatorProfile = hasManagementPermission(user);

    let updateFilterOnSearchChange = useRef(false);
    const features = tariff ? tariff.features : []

    useEffect(() => {
        fetchData();
    }, [pageable.sort, filters]);

    useEffect(() => {

        //without this logic on reset filters makes 2 api calls
        if (!updateFilterOnSearchChange.current) {
            updateFilterOnSearchChange.current = true;
            return
        }

        const timeout = setTimeout(() => {
            updateFilter('operator', operatorSearchValue);
        }, 300)

        return () => {
            clearTimeout(timeout);
        }
    }, [operatorSearchValue])

    function updateFilter(key, val) {
        setFilters({
            ...filters,
            [key]: val
        })
    }

    function fetchData() {
        setIsFetching(true);

        getOperatorsTableData(company.id, filters, pageable).then((res) => {
            if (res) {
                setOperators(res);
            } else {
                setOperators([])
            }
        }).catch(e => {
            setOperators([])
        }).finally(() => {
            setIsFetching(false);
        })
    }

    function resetFilters() {
        updateFilterOnSearchChange.current = false
        setOperatorSearchValue('')

        setFilters({
            ...filters,
            start: null,
            end: null,
            operator: ''
        })
    }

    const exportToExcel = () => {
        let columns = [];

        if (activeTab === tabOptions.general) {
            columns = [
                {label: 'Operator', value: row => `${row.firstName} ${row.lastName}`},
                {label: 'Status', value: 'currentStatus'},
                {label: 'Status Time', value: 'currentStatusTime'},
                {
                    label: 'Rating / Total',
                    value: row => {
                        const total = row.ratingCount;
                        const rating = row.rating;
                        return `${rating}/${total}`;
                    },
                },
                {
                    label: 'Current Chat Limit',
                    value: row => row.currentChatLimit >= 9999 ? 'Unlimited' : row.currentChatLimit
                },
                {
                    label: 'Chat Limit',
                    value: row => row.chatLimit >= 9999 ? 'Unlimited' : row.chatLimit
                },
            ];
        } else if (activeTab === tabOptions.requests) {
            columns = [
                {label: 'Operator', value: row => `${row.firstName} ${row.lastName}`},
                {label: 'All Chats', value: 'allChats'},
                {label: 'Answered', value: 'answeredChats'},
                {label: 'Unanswered', value: 'unansweredChats'},
                {label: 'Active', value: 'activeChats'},
                {label: 'Chats Today', value: 'todaysChats'},
                features.includes(config.features.CONVERSATION_FORWARD) ? {
                    label: 'Forwarded',
                    value: 'forwardedChats'
                } : {},
            ]
            ;
        } else if (activeTab === tabOptions.time) {
            columns = [
                {label: 'Operator', value: row => `${row.firstName} ${row.lastName}`},
                {
                    label: 'Avg Answer Speed',
                    value: row => formatSecondsToHHMMSS(row.avrgAnswerSpeed),
                },
                {
                    label: 'Avg First Answer Speed',
                    value: row => formatSecondsToHHMMSS(row.avrgFirstAnswerSpeed),
                },
                {
                    label: 'Avg Conversation Time',
                    value: row => formatSecondsToHHMMSS(row.avrgConvTime),
                },
            ];
        }

        globalExportToExcel({
            data: operators,
            columns,
            fileName: `Operators-${activeTab}.xlsx`,
        });
    };

    const renderHeader = () => {
        const headers = tableHeaders[activeTab];

        if (!headers) return null;

        return (
            <tr>
                {headers.map((header, index) => (
                    <th
                        key={header.text}
                        style={{textAlign: header.align || 'left', cursor: 'pointer', width: header.width}}
                        onClick={() => handleSort(header.sortColumn, pageable, setPageable)}
                    >
                        <div
                            className="operators-table-header"
                            style={{justifyContent: index === 0 ? 'flex-start' : 'center'}}
                        >
                            <Tooltip
                                text={t({user, text: header.text})}
                                needTranslate={false}
                                style={{flex: '1 1 auto', minWidth: 0}}
                            >
                                <span
                                    className="operators-table-header__label"
                                    style={{textAlign: header.align || 'left'}}
                                >
                                    <Translate text={header.text}/>
                                </span>
                            </Tooltip>
                            {
                                header.sortColumn &&

                                <TableHeaderSort
                                    isActive={pageable.sort.split(',').slice(0, -1).join(',') === header.sortColumn}
                                    direction={pageable.sort.split(',').pop()}
                                />
                            }
                        </div>

                    </th>
                ))}
            </tr>
        );
    };

    const renderRows = () => {
        return operators.map((operator, index) => {

            const fullName = `${operator.firstName} ${operator.lastName}`;

            const ratingInStars = (operator.rating * 5) / 100;


            // worked out by the server against everyone in this company over
            // the period being looked at - see OperatorScoring
            const rank = operator.level;

            if (activeTab === tabOptions.general) {
                return (
                    <tr key={index}>
                        <td>
                            <OperatorProfileLink
                                operator={operator}
                                canOpen={canViewOperatorProfile}
                                onOpen={setOperatorProfile}
                                className="statistics-operator-cell"
                            >
                                <ContactAvatar contact={operator} size={32}/>
                                <span>{fullName}</span>
                            </OperatorProfileLink>
                        </td>
                        <td style={{textAlign: 'center'}}>
                              <span style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  backgroundColor: '#f9f9f9',
                                  padding: '4px 8px',
                                  borderRadius: '6px',
                                  fontSize: '14px',
                                  color: 'var(--ds-text-primary)'
                              }}>

                                  <ColorIndicator currentStatus={operator.currentStatus}/>
                                  <Translate text={operator.currentStatus}/> ({operator.currentStatusTime})
                              </span>
                        </td>
                        <td style={{textAlign: 'center', cursor: 'pointer'}}>
                            <div
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    backgroundColor: '#f9f9f9',
                                    padding: '4px 8px',
                                    borderRadius: '6px',
                                    fontSize: '14px',
                                    color: 'var(--ds-text-primary)',
                                    maxWidth: '100%',
                                    boxSizing: 'border-box'
                                }}
                            >
                                <RatingStars value={ratingInStars}/>
                                <div style={{display: 'flex', gap: '5px'}}>
                                    <Tooltip text={'Rating'}>{operator.rating}%</Tooltip>
                                    /
                                    <Tooltip text={'ratingCount'}>{operator.ratingCount}</Tooltip>
                                </div>

                                {/* beside the figures rather than third in the
                                    list of them, and labelled Level like every
                                    other place it is drawn - it was the only one
                                    that wedged the badge between slashes and
                                    called it something else */}
                                <Tooltip ri text="Level" style={{display: 'flex'}}>
                                    <LevelBadge level={rank}/>
                                </Tooltip>
                            </div>
                        </td>
                        <td style={{textAlign: 'center'}}>{operator.currentChatLimit >= 9999 ?
                            <Translate text={'unlimited'}/> : operator.currentChatLimit}</td>
                        <td style={{textAlign: 'center'}}>{operator.chatLimit >= 9999 ?
                            <Translate text={'unlimited'}/> : operator.chatLimit}</td>
                    </tr>
                );
            }

            if (activeTab === tabOptions.requests) {
                return (
                    <tr key={index}>
                        <td>
                            <OperatorProfileLink
                                operator={operator}
                                canOpen={canViewOperatorProfile}
                                onOpen={setOperatorProfile}
                                className="statistics-operator-cell"
                            >
                                <ContactAvatar contact={operator} size={32}/>
                                <span>{fullName}</span>
                            </OperatorProfileLink>
                        </td>

                        <td
                            className="clickable-td"
                            style={{textAlign: 'center'}}
                            onClick={() => openTrModal('allRequestsToOperator', {
                                operatorId: operator.id,
                                operator: operator.firstName + ' ' + operator.lastName,
                                end: filters.end,
                                start: filters.start
                            })}
                        >
                            {operator.allChats}
                        </td>

                        <td
                            className="clickable-td"
                            style={{textAlign: 'center'}}
                            onClick={() => openTrModal('todaysRequestsToOperator', {
                                operatorId: operator.id,
                                operator: operator.firstName + ' ' + operator.lastName,
                                end: filters.end,
                                start: filters.start
                            })}
                        >
                            {operator.todaysChats}
                        </td>

                        <td
                            className="clickable-td"
                            style={{textAlign: 'center', color: '#22c55e'}}
                            onClick={() => openTrModal('answeredRequestsToOperator', {
                                operatorId: operator.id,
                                operator: operator.firstName + ' ' + operator.lastName,
                                unanswered: false,
                                end: filters.end,
                                start: filters.start
                            })}
                        >
                            {operator.answeredChats}
                        </td>

                        <td
                            className="clickable-td"
                            style={{textAlign: 'center', color: '#E14E52'}}
                            onClick={() => openTrModal('unansweredRequestsToOperator', {
                                operatorId: operator.id,
                                operator: operator.firstName + ' ' + operator.lastName,
                                unanswered: true,
                                end: filters.end,
                                start: filters.start
                            })}
                        >
                            {operator.unansweredChats}
                        </td>

                        <td
                            className="clickable-td"
                            style={{textAlign: 'center'}}
                            onClick={() => openTrModal('activeRequests', {
                                operatorId: operator.id,
                                operator: operator.firstName + ' ' + operator.lastName,
                                statuses: ['OPERATOR_PROCESSING'],
                                end: filters.end,
                                start: filters.start
                            })}
                        >
                            {operator.activeChats}
                        </td>

                        {features.includes(config.features.CONVERSATION_FORWARD) && (
                            <td
                                className="clickable-td"
                                style={{textAlign: 'center'}}
                                onClick={() => openTrModal('forwardedRequestsToOperator', {
                                    operatorId: operator.id,
                                    operator: operator.firstName + ' ' + operator.lastName,
                                    forwarded: true,
                                    end: filters.end,
                                    start: filters.start
                                })}
                            >
                                {operator.forwardedChats}
                            </td>
                        )}

                    </tr>
                );
            }

            if (activeTab === tabOptions.time) {
                return (
                    <tr key={index}>
                        <td>
                            <OperatorProfileLink
                                operator={operator}
                                canOpen={canViewOperatorProfile}
                                onOpen={setOperatorProfile}
                                className="statistics-operator-cell"
                            >
                                <ContactAvatar contact={operator} size={32}/>
                                <span>{fullName}</span>
                            </OperatorProfileLink>
                        </td>
                        <td style={{textAlign: 'center'}}>{formatSecondsToHHMMSS(operator.avrgAnswerSpeed)}</td>
                        <td style={{textAlign: 'center'}}>{formatSecondsToHHMMSS(operator.avrgFirstAnswerSpeed)}</td>
                        <td style={{textAlign: 'center'}}>{formatSecondsToHHMMSS(operator.avrgConvTime)}</td>
                    </tr>
                );
            }

            return null;
        });
    };

    return (
        <div style={{width: '100%'}}>

            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '10px',
                width: '100%',
                marginBottom: '16px',
                flexWrap: 'wrap',
            }}>
                <div style={{display: 'flex', gap: '10px', flexWrap: 'wrap'}}>
                    <SearchInput
                        value={operatorSearchValue}
                        onChange={(e) => setOperatorSearchValue(e.target.value)}
                        placeholder={'searchByOperator'}
                        style={{width: '300px', height: '40px'}}
                    />

                    {(activeTab === tabOptions.requests || activeTab === tabOptions.time) &&
                        <React.Fragment>
                            <DatePicker
                                inputContainerStyle={{width: '240px', height: '40px'}}
                                value={filters.start}
                                onChange={e => updateFilter('start', e)}
                                onReset={() => updateFilter('start', null)}
                                disabled={isFetching}
                                placeholder={'startDate'}
                                panelAlign={{horizontal: 'right', vertical: ''}}
                            />
                            <DatePicker
                                inputContainerStyle={{width: '240px', height: '40px'}}
                                value={filters.end}
                                onChange={e => updateFilter('end', e)}
                                onReset={() => updateFilter('end', null)}
                                placeholder={'endDate'}
                                endOfDay
                                disabled={isFetching}
                            />
                        </React.Fragment>
                    }
                    {
                        (filters.start !== null || filters.end !== null || filters.operator !== '')
                        &&
                        <ResetButton
                            onClick={resetFilters}
                            width={150}
                            height={40}
                            variant="outline"
                        />
                    }

                </div>

                <div>
                    <ExcelButton
                        onClick={exportToExcel}
                        width={190}
                        height={40}
                        variant="outline"
                        children={
                            <span style={{fontSize: '12px', backgroundColor: 'transparent'}}>
                                        <Translate text={'downloadXls'}/>
                                    </span>
                        }
                    />
                </div>
            </div>

            <Tabs
                selectedTab={activeTab}
                onTabChange={setActiveTab}
                tabs={Object.values(tabOptions)}
                style={{justifyContent: 'start', margin: '0 0 16px 0'}}
                variant='underline'
            />

            <TableScroll cellHover={activeTab === tabOptions.requests} style={{height: '400px'}}>
                <table
                    className="custom-table"
                    style={{
                        minWidth: activeTab === tabOptions.requests
                            ? '1380px'
                            : activeTab === tabOptions.general ? '1200px' : '900px'
                    }}
                >
                    <thead>{renderHeader()}</thead>
                    <tbody>
                    {(isFetching) ? (
                        <tr>
                            <td colSpan="100%" style={{padding: '50px 0'}}><LoadingComponent/></td>
                        </tr>
                    ) : (
                        renderRows()
                    )}

                    {
                        operators.length === 0 && !isFetching &&
                        <tr className="table-no-result">
                            <td colSpan={tableHeaders[activeTab].length}
                                style={{textAlign: 'center', padding: '40px', color: Colors.silverWhiteSemiDark}}>
                                <Translate text={'noResultsFound'}/>
                            </td>
                        </tr>
                    }
                    </tbody>
                </table>
            </TableScroll>

            {/*
                Given the table's own filters, so the profile answers for the
                same companies and the same dates the row was read under. A
                figure in the drawer that disagrees with the figure beside it in
                the table reads as one of the two being wrong.
            */}
            <OperatorProfileDrawer
                open={!!operatorProfile}
                onClose={() => setOperatorProfile(null)}
                operator={operatorProfile}
                parentCompanyId={company.id}
                filters={filters}
            />
        </div>
    );
};

export default OperatorsTable;



// WEBPACK FOOTER //
// ./src/scenes/dashboard/components/management/statistics/components/operators/OperatorsTable.jsx